import React, { useState, useEffect } from "react";
import moment from "moment";
import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import consts from "../../../consts.js";
import api from "../../../services/api";
import HeaderLogado from "../../../components/common/Authenticated/Header/HeaderLogado";
import BottomMenu from "../../../components/common/Authenticated/Menu/Menu";
import DateTime from "../../../components/Agendamento/Datetime";
import EspecialistaPicker from "../../../components/Agendamento/EspecialistaPicker.js";
import PaymentPicker from "../../../components/Agendamento/PaymentPicker";
import LoadingComponent from "../../../components/common/LoadingComponent.js";

function Agendamento() {
  const { servicoId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const usuarioStorage = JSON.parse(localStorage.getItem("usuario")) || {};

  const [servico, setServico] = useState(null);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAgendamento, setLoadingAgendamento] = useState(false);
  const [horariosDisponiveis, setHorariosDisponiveis] = useState([]);
  const [cartoes, setCartoes] = useState([]);

  const [agendamento, setAgendamento] = useState({
    servicoId,
    colaboradorId: "",
    data: "", // não inicializa com moment
    preco: 0,
    cartaoId: "",
    cartaoCVV: "",
  });

  const [dataSelecionada, setDataSelecionada] = useState("");
  const [horaSelecionada, setHoraSelecionada] = useState("");

  const Voltar = () => {
    navigate("/area-restrita/");
  };

  // Busca dados do serviço
  useEffect(() => {
    const fetchServico = async () => {
      try {
        const { data } = await api.get(
          `servico/salao/${consts.salaoId}/${servicoId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!data.error && data.servico) {
          setServico(data.servico);

          setAgendamento((prev) => ({
            ...prev,
            preco: data.servico.preco,
          }));

          setColaboradores(
            data.servico.servicoEspecificidades.map((esp) => ({
              _id: esp.colaboradorId._id,
              nome: esp.colaboradorId.nome,
              foto: esp.colaboradorId.foto,
              preco: esp.preco,
            }))
          );
        }
      } catch (err) {
        console.error("Erro ao buscar serviço:", err);
      }
    }

    fetchServico();
  }, [consts.salaoId, servicoId, token]);

  // Busca agenda/horários disponíveis
  useEffect(() => {
    const fetchAgenda = async () => {
      try {
        const { data } = await api.post(
          "/agendamento/dias-disponiveis/",
          {
            salaoId: consts.salaoId,
            servicoId,
            data: moment().format("YYYY-MM-DD"),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!data.error && data.agenda) {
          const agendaFormatada = data.agenda.map((item) => {
            const date = Object.keys(item)[0];
            const colaboradoresDoDia = Object.values(item)[0];
            return { [date]: colaboradoresDoDia };
          });

          setHorariosDisponiveis(agendaFormatada);
        }
      } catch (err) {
        console.error("Erro ao buscar agenda:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAgenda();
  }, [consts.salaoId, servicoId, token]);

  // Busca cartões do usuário
  useEffect(() => {
    const fetchCartoes = async () => {
      try {
        const { data } = await api.get(`/cliente/cartoes/${usuarioStorage._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!data.error && data.cartao?.data?.data) {
          const cartoesFormatados = data.cartao.data.data.map((c) => ({
            _id: c.id,
            numeroFinal: c.last_four_digits,
            bandeira: c.brand,
            holderName: c.holder_name,
            expMonth: c.exp_month,
            expYear: c.exp_year,
            status: c.status,
            type: c.type,
          }));
          setCartoes(cartoesFormatados);
        }
      } catch (err) {
        console.error("Erro ao buscar cartões:", err);
      }
    };

    fetchCartoes();
  }, [token]);

  // Filtra especialistas disponíveis
  const especialistasDisponiveis = colaboradores.filter((colab) => {
    if (!dataSelecionada) return true;

    const dia = horariosDisponiveis.find((item) => Object.keys(item)[0] === dataSelecionada);
    if (!dia) return false;

    const horariosDoColab = dia[dataSelecionada][colab._id] || [];
    if (!horaSelecionada) return horariosDoColab.length > 0;

    return horariosDoColab.some((slot) => slot[0] === horaSelecionada);
  });

  const handleSelectEspecialista = (colaboradorId) => {
    const colab = colaboradores.find((c) => c._id === colaboradorId);
    if (colab) {
      setAgendamento((prev) => ({
        ...prev,
        colaboradorId,
        preco: colab.preco,
      }));
    }
  };

  useEffect(() => {
    if (agendamento.colaboradorId) {
      const estaDisponivel = especialistasDisponiveis.some(
        (c) => c._id === agendamento.colaboradorId
      );
      if (!estaDisponivel) {
        setAgendamento((prev) => ({
          ...prev,
          colaboradorId: "",
          preco: servico ? servico.preco : 0,
        }));
      }
    }
  }, [dataSelecionada, horaSelecionada, especialistasDisponiveis, servico, agendamento.colaboradorId]);

  // Função para criar agendamento no servidor
  const confirmarAgendamento = async () => {
    if (!dataSelecionada || !horaSelecionada || !agendamento.colaboradorId || !agendamento.cartaoId) {
      alert("Selecione dia, horário, especialista e cartão.");
      return;
    }

    try {
      setLoadingAgendamento(true);

      const dataHoraBrasil = moment.tz(
      `${dataSelecionada} ${horaSelecionada}`,
      "YYYY-MM-DD HH:mm",
      "America/Sao_Paulo"
    ).toDate();

      const response = await api.post(
        "/agendamento",
        {
          clienteId: usuarioStorage._id,
          salaoId: consts.salaoId,
          servicoId: agendamento.servicoId,
          colaboradorId: agendamento.colaboradorId,
          cartaoId: agendamento.cartaoId,
          cartaoCVV: agendamento.cartaoCVV,
          data: dataHoraBrasil,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.error) {
        alert(response.data.message || "Erro ao criar agendamento.");
      } else {
        alert("Agendamento confirmado!");
        navigate("/area-restrita/");
      }
    } catch (err) {
      console.error("Erro ao criar agendamento:", err);
      alert("Erro ao criar agendamento. Tente novamente.");
    } finally {
      setLoadingAgendamento(false);
    }
  };

  if (loading) {
    return (
    <div className="bg-gray-100 min-h-svh">
      <div className="flex flex-col items-center justify-center h-screen">
        <LoadingComponent/>
        <h2 className="text-xl font-bold mb-2">Só um instante...</h2>
        <p>Estamos buscando as informações do serviço...</p>
      </div>
    </div>
    );
  }

  return (
    <>
      <HeaderLogado />
      <div className="bg-white min-h-svh">
        <button
        onClick={Voltar}
        className="relative top-2 left-2 bg-[#003D42] p-2 rounded-full z-10 flex items-center justify-center shadow-md w-9 h-9 hover:bg-[#00575e] transition"
      >
        <ChevronLeft size={20} color="#fff" />
      </button> 
        <div className="max-w-4xl mx-auto p-6 bg-white pb-24">
          {servico ? (
            <>
              <div className="flex bg-gray-100 p-4 rounded-md mb-6" style={{ gap: 20 }}>
                <img
                  src={
                    servico.arquivos?.[0]
                      ? `${consts.bucketUrl}/${servico.arquivos[0].caminho}`
                      : "https://www.gravatar.com/avatar/?d=mp&s=200"
                  }
                  alt={servico?.titulo}
                  className="w-32 h-32 object-cover rounded"
                />
                <div className="flex flex-col justify-center">
                  <h2 className="text-2xl font-bold">{servico?.titulo}</h2>
                  <p className="text-gray-700 mt-2">Total: R$ {agendamento.preco}</p>
                </div>
              </div>

              {colaboradores.length > 0 && horariosDisponiveis.length > 0 ? (
                <>
                  <DateTime
                    datas={horariosDisponiveis}
                    dataSelecionada={dataSelecionada}
                    setDataSelecionada={setDataSelecionada}
                    horaSelecionada={horaSelecionada}
                    setHoraSelecionada={setHoraSelecionada}
                  />

                  <div className="mb-4">
                    <h3 className="font-semibold">Escolha o Especialista:</h3>
                    <EspecialistaPicker
                      colaboradores={especialistasDisponiveis}
                      agendamento={agendamento}
                      onSelect={handleSelectEspecialista}
                    />
                  </div>

                  <div className="mb-4">
                    <h3 className="font-semibold">Escolha o cartão de pagamento:</h3>
                    <PaymentPicker
                      cartoes={cartoes}
                      agendamento={agendamento}
                      setAgendamento={setAgendamento}
                    />

                    <button
                      onClick={confirmarAgendamento}
                      disabled={loadingAgendamento}
                      className={`mt-6 w-full bg-[#003D42] text-white font-semibold py-3 rounded-md shadow-md hover:bg-[#002d30] transition ${
                        loadingAgendamento ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {loadingAgendamento ? "Finalizando..." : "Finalizar agendamento"}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-center text-red-500">
                  Desculpe, mas não temos nenhum horário disponível.
                </p>
              )}
            </>
          ) : (
            <p className="text-center text-red-500">Serviço não encontrado.</p>
          )}
          </div>
      </div>
      <BottomMenu />
    </>
  );
}

export default Agendamento;
